import React, { useState, useEffect } from 'react';

const ImageCarousel = ({ images, logo, name, homeName, initialIndex = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [prevIndex, setPrevIndex] = useState(currentIndex);
    const [isAnimating, setIsAnimating] = useState(false);
    const [slideDirection, setSlideDirection] = useState('');

    useEffect(() => {
        if (currentIndex !== prevIndex) {
            // Determine slide direction
            const direction = currentIndex > prevIndex ? 'right' : 'left';
            setSlideDirection(direction);
            setIsAnimating(true);

            // Reset animation state after animation completes
            const timer = setTimeout(() => {
                setIsAnimating(false);
                setPrevIndex(currentIndex);
            }, 1000); // Match this to animation duration

            return () => clearTimeout(timer);
        }
    }, [currentIndex, prevIndex]);

    // Autoplay: advance slide every 4 seconds
    useEffect(() => {
        const totalImages = Array.isArray(images) ? images.length : 0;
        if (totalImages <= 1) return;

        const autoplayTimer = setTimeout(() => {
            if (!isAnimating) {
                setCurrentIndex((prev) => (prev + 1) % totalImages);
            }
        }, 4000);

        return () => clearTimeout(autoplayTimer);
    }, [currentIndex, isAnimating, images]);

    const handlePrev = () => {
        if (!isAnimating) {
            setCurrentIndex((prev) =>
                (prev - 1 + images.length) % images.length
            );
        }
    };

    const handleNext = () => {
        if (!isAnimating) {
            setCurrentIndex((prev) =>
                (prev + 1) % images.length
            );
        }
    };

    return (
        <div className="relative">
            {/* Navigation Arrows */}
            <button
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white w-10 h-10 flex items-center justify-center shadow-md hover:bg-gray-50 transition-colors"
                onClick={handlePrev}
                aria-label="Previous image"
                disabled={isAnimating}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-turquoise">
                    <path d="M15 18l-6-6 6-6" />
                </svg>
            </button>
            <button
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white w-10 h-10 flex items-center justify-center shadow-md hover:bg-gray-50 transition-colors"
                onClick={handleNext}
                aria-label="Next image"
                disabled={isAnimating}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-turquoise">
                    <path d="M9 18l6-6-6-6" />
                </svg>
            </button>

            {/* Image Container */}
            <div className="w-full h-0 pb-[68%] relative shadow-lg overflow-hidden">
                {/* Current Image */}
                <img
                    src={images[currentIndex]}
                    alt={`${name} - ${homeName}`}
                    className={`absolute top-0 left-0 w-full h-full object-cover animated ${
                        isAnimating && slideDirection === 'right' ? 'fadeInRight' :
                        isAnimating && slideDirection === 'left' ? 'fadeInLeft' : ''
                    }`}
                    style={{
                        zIndex: isAnimating ? 2 : 1
                    }}
                />

                {/* Previous Image (for transition) */}
                {isAnimating && (
                    <img
                        src={images[prevIndex]}
                        alt={`${name} - ${homeName} (previous)`}
                        className={`absolute top-0 left-0 w-full h-full object-cover animated ${
                            slideDirection === 'right' ? 'fadeOutLeft' : 'fadeOutRight'
                        }`}
                        style={{ zIndex: 1 }}
                    />
                )}

                {/* Preload next and previous images */}
                <div className="hidden">
                    {images.map((src, idx) => (
                        idx !== currentIndex && idx !== prevIndex ?
                        <img key={idx} src={src} alt="Preload" /> : null
                    ))}
                </div>

                <div className="absolute top-4 right-4 z-10">
                    <img src={logo} alt={name} className="h-14 md:h-40 w-auto object-contain" />
                </div>
            </div>
        </div>
    );
};

export default ImageCarousel;